/**
 * TtsPlaybackService — module-singleton that plays Polly-synthesised MP3
 * audio via the Web Audio API (AudioContext + AudioBufferSourceNode).
 *
 * Why Web Audio API and not HTMLAudioElement:
 *   • CIMA's CloudFront response-headers policy serves a strict CSP whose
 *     `default-src 'self'` (no `media-src`) blocks blob: URLs assigned to
 *     <audio src=...>. Both Firefox and Chrome refuse with NotSupportedError.
 *   • AudioContext.decodeAudioData accepts ArrayBuffer directly — no URL,
 *     so CSP media-src doesn't apply.
 *
 * Session model (mute/unmute = pause/resume):
 *   AudioBufferSourceNode has no native pause. Each speakText call owns a
 *   "session" holding the decoded buffer plus the elapsed offset. Mute
 *   stops the active source and records how far we got; unmute creates a
 *   fresh source and starts it at the recorded offset. The original
 *   speakText Promise stays unresolved across pause/resume cycles and
 *   only resolves when the buffer plays through naturally.
 *
 * Autoplay handling:
 *   Browsers may suspend the AudioContext until a user gesture. We try
 *   ctx.resume() once; if that doesn't bring it to 'running' we throw a
 *   code:'autoplay-blocked' error so the caller can show a "click to
 *   enable" prompt and retry on the next gesture.
 *
 * Mute is global (per-user). Persisted in localStorage so the choice
 * survives reloads. Default: muted (user opts in).
 */
import { synthesise } from '../../api/voice';

const MUTE_STORAGE_KEY = 'cima:tts:muted';
const DEFAULT_MUTED = true;   // opt-in TTS — never autoplay surprises

// One in-flight speakText call's playback state. Survives mute/unmute
// cycles — `source` is null when paused, set when actively playing. The
// `offset` advances every time we pause so resume picks up where we left
// off rather than restarting from zero.
interface PlaybackSession {
  buffer: AudioBuffer;
  offset: number;          // seconds into buffer where the next start() should begin
  source: AudioBufferSourceNode | null;
  startedCtxTime: number;  // ctx.currentTime captured when this `source` started
  resolve: () => void;
  reject: (err: any) => void;
}

class TtsPlaybackService {
  private audioContext: AudioContext | null = null;
  private muted: boolean;
  // Bumped on every speakText call. Lets us ignore late-arriving
  // synthesise() promises after the user moved on / called speakText again.
  private generation = 0;
  private session: PlaybackSession | null = null;
  // Set true by pause(), checked in onended to distinguish a pause-stop
  // from a natural end-of-buffer.
  private pauseRequested = false;

  constructor() {
    let initial = DEFAULT_MUTED;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(MUTE_STORAGE_KEY);
      if (stored === '0') initial = false;
      else if (stored === '1') initial = true;
    }
    this.muted = initial;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Set mute state and persist. Pauses (preserving offset) instead of
   * destroying the session, so a subsequent unmute resumes from the same
   * point in the agent's reply.
   */
  setMuted(muted: boolean): void {
    if (muted === this.muted) return;
    this.muted = muted;
    try { localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
    if (muted) {
      console.debug('[tts] mute → pause');
      this.pausePlayback();
    } else if (this.session && !this.session.source) {
      console.debug('[tts] unmute → resume from offset', { offset: this.session.offset.toFixed(2) });
      this.startPlayback();
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** Hard stop — discard any session entirely. The pending speakText
   *  promise rejects with err.code='stopped' so callers can tell apart
   *  a user-interrupted clip from one that ended naturally. */
  stop(): void {
    this.generation++;
    this.endSession();
  }

  private endSession(): void {
    if (this.session) {
      const sess = this.session;
      this.session = null;
      if (sess.source) {
        try { sess.source.stop(); } catch { /* ignore */ }
        try { sess.source.disconnect(); } catch { /* ignore */ }
      }
      const e: any = new Error('TTS stopped');
      e.code = 'stopped';
      sess.reject(e);
    }
    this.pauseRequested = false;
  }

  private getContext(): AudioContext {
    if (!this.audioContext) {
      const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) throw new Error('Web Audio API not supported');
      this.audioContext = new Ctor();
    }
    return this.audioContext!;
  }

  /**
   * Synthesise + play `text`. Returns a Promise that resolves only after
   * the buffer plays through (which may be many real-world seconds later
   * if the user paused via mute mid-clip and unmuted afterwards). When
   * speakText is called a second time mid-playback the prior session is
   * discarded — barge-in friendly.
   */
  async speakText(text: string): Promise<void> {
    if (!text.trim()) {
      console.debug('[tts] skip: empty text');
      return;
    }
    if (this.muted) {
      console.debug('[tts] skip: muted at call time');
      return;
    }

    // Discard any prior session and bump generation
    this.endSession();
    const myGen = ++this.generation;
    console.debug('[tts] speakText start', { gen: myGen, len: text.length });

    let response;
    try {
      const t0 = performance.now();
      response = await synthesise(text);
      console.debug('[tts] synthesise ok', {
        ms: Math.round(performance.now() - t0),
        voiceId: response.voiceId,
        engine: response.engine,
        audioLen: response.audio.length,
      });
    } catch (err: any) {
      if (myGen !== this.generation) return;
      console.error('[tts] synthesise failed:', err?.message || err);
      throw err;
    }
    if (myGen !== this.generation) return;

    // Decode base64 → ArrayBuffer (no URL, so CSP media-src doesn't apply).
    const binary = atob(response.audio);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    let ctx: AudioContext;
    try {
      ctx = this.getContext();
    } catch (err: any) {
      console.error('[tts] AudioContext unavailable:', err?.message || err);
      throw err;
    }

    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (err) { console.warn('[tts] resume rejected', err); }
    }
    if (ctx.state !== 'running') {
      console.warn('[tts] AudioContext still suspended — autoplay blocked', { state: ctx.state });
      const e: any = new Error('Browser blocked audio autoplay — click anywhere to enable voice.');
      e.code = 'autoplay-blocked';
      throw e;
    }
    if (myGen !== this.generation) return;

    let audioBuffer: AudioBuffer;
    try {
      // decodeAudioData mutates the ArrayBuffer in some implementations;
      // pass a slice() copy to be safe.
      audioBuffer = await ctx.decodeAudioData((bytes.buffer as ArrayBuffer).slice(0));
    } catch (err: any) {
      console.error('[tts] decodeAudioData failed:', err?.message || err);
      throw err;
    }
    if (myGen !== this.generation) return;
    console.debug('[tts] decoded', {
      duration: Number(audioBuffer.duration.toFixed(2)),
      channels: audioBuffer.numberOfChannels,
      sampleRate: audioBuffer.sampleRate,
    });

    return new Promise<void>((resolve, reject) => {
      this.session = {
        buffer: audioBuffer,
        offset: 0,
        source: null,
        startedCtxTime: 0,
        resolve,
        reject,
      };
      // If user toggled mute between synthesise + decode, the session
      // sits dormant until they unmute (which calls startPlayback for us).
      if (this.muted) {
        console.debug('[tts] decoded but muted — session waiting for unmute');
        return;
      }
      this.startPlayback();
    });
  }

  /** Start (or resume) playback from `session.offset`. */
  private startPlayback(): void {
    const sess = this.session;
    if (!sess) return;
    if (sess.source) return; // already playing
    if (sess.offset >= sess.buffer.duration) {
      // Already past end — finish the session cleanly
      const resolveFn = sess.resolve;
      this.session = null;
      resolveFn();
      return;
    }

    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* surfaced on next attempt */ });
    }

    const source = ctx.createBufferSource();
    source.buffer = sess.buffer;
    source.connect(ctx.destination);
    this.pauseRequested = false;

    source.onended = () => {
      // Ignore stale callbacks from sources we already replaced
      if (this.session !== sess || sess.source !== source) return;
      try { source.disconnect(); } catch { /* ignore */ }

      if (this.pauseRequested) {
        // We stopped this source as part of a mute. Capture how far we
        // got, clear `source`, and leave the session alive so unmute can
        // resume from the new offset.
        const elapsed = ctx.currentTime - sess.startedCtxTime;
        sess.offset = Math.min(sess.offset + elapsed, sess.buffer.duration);
        sess.source = null;
        this.pauseRequested = false;
        console.debug('[tts] paused', { offset: sess.offset.toFixed(2) });
        return;
      }

      // Natural end-of-buffer
      console.debug('[tts] playback ended');
      const resolveFn = sess.resolve;
      this.session = null;
      resolveFn();
    };

    try {
      source.start(0, sess.offset);
      sess.startedCtxTime = ctx.currentTime;
      sess.source = source;
      console.debug('[tts] playback started', { fromOffset: sess.offset.toFixed(2) });
    } catch (err: any) {
      console.error('[tts] start failed:', err?.message || err);
      const rejectFn = sess.reject;
      this.session = null;
      rejectFn(err);
    }
  }

  /** Stop the active source while keeping the session for later resume. */
  private pausePlayback(): void {
    if (!this.session?.source) return;
    this.pauseRequested = true;
    try { this.session.source.stop(); } catch { /* ignore */ }
    // onended fires synchronously-ish and updates session.offset
  }
}

// Module-level singleton — one global TTS service for the whole app.
let _instance: TtsPlaybackService | null = null;
export function getTts(): TtsPlaybackService {
  if (!_instance) _instance = new TtsPlaybackService();
  return _instance;
}
